import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  PAYMENT_GATEWAY,
  TEST_RECORD_PAYMENT_TOKEN,
  TEST_RECORD_SUCCEEDED_STATUS,
} from '../../constants';
import type { Environment, UseCase } from '../../entities/enums';
import { CREATE_2C2P_PAYMENT_INTENT } from '../../graphql';
import { CREATE_2C2P_INVOICE_PAYMENT_INTENT } from '../../graphql/Mutations';
import { PAYMENT_2C2P_INVOICE_INQUIRY } from '../../graphql/Queries';
import translations from '../../locales.vocab';
import type {
  HirerPaymentResult,
  ICreate2C2PPaymentIntentForInvoicePayload,
  Invoice,
  PaymentStatus,
} from '../../types';
import { redirectCartidQuery, redirectQrisQuery } from '../../utils/constants';
import getReturnUrl from '../../utils/getReturnUrl';
import { wait } from '../../utils/wait';
import {
  PaymentGeneralError,
  PaymentDefaultError,
} from '../Errors/customErrors';
import { hideQrisAtom } from '../Maintenance/MaintenanceStore';

import {
  validateQRModalIsClosed,
  validateQRModalIsExpired,
} from './QrisCheckStatus';
import { qrisDataAtom } from './QrisStore';
import {
  INTERVAL,
  getToken,
  setIsClosed,
  setIsExpired,
  setToken,
} from './QrisVariables';

interface InvoiceInputs {
  customerId: string;
  accountId: string;
  prn: string;
  invoice: Invoice[];
  email: string;
  totalForPaymentIntent: number;
}

interface CajaInputs {
  cartId: string;
  referenceId: string;
}
export interface UseQrisInput<T> {
  useCase: UseCase;
  env: Environment;
  invoiceInputs: T extends 'INVOICE' ? InvoiceInputs : undefined;
  cajaInputs: T extends 'CAJA' ? CajaInputs : undefined;
}

export interface QrisData {
  qrImageUrl: string;
  qrExpiry: string;
  paymentToken: string;
  reference: string;
  redirectUrl: string;
}

// Hook to be used for general QRIS payment method not specific to any consumers
const useQris = ({
  useCase,
  env,
  invoiceInputs = {
    customerId: '',
    accountId: '',
    prn: '',
    invoice: [],
    email: '',
    totalForPaymentIntent: 0,
  },
  cajaInputs = {
    cartId: '',
    referenceId: '',
  },
}: UseQrisInput<UseCase>) => {
  const { cartId, referenceId } = cajaInputs;
  const { customerId, accountId, prn, invoice, email, totalForPaymentIntent } =
    invoiceInputs;

  const setQrisData = useSetAtom(qrisDataAtom);
  const [create2C2PPaymentIntent] = useMutation(CREATE_2C2P_PAYMENT_INTENT);
  const [create2C2PInvoicePaymentIntent] = useMutation(
    CREATE_2C2P_INVOICE_PAYMENT_INTENT,
  );

  const [getPaymentInquiry] = useLazyQuery(PAYMENT_2C2P_INVOICE_INQUIRY, {
    fetchPolicy: 'no-cache',
  });
  const isMaintenance = useAtomValue(hideQrisAtom);
  const { t } = useTranslations(translations);

  const submitQrisPayment = async (): Promise<HirerPaymentResult> => {
    resetStates();
    // TODO: Tracking

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY['2c2p'],
      [redirectQrisQuery, redirectCartidQuery(cartId)],
      useCase,
      env,
    );

    const response = await create2C2PPaymentIntent({
      variables: {
        input: {
          orderId: referenceId,
          paymentMethodType: 'QR',
          frontendReturnUrl,
          channelCode: 'SHPQR',
        },
      },
    });

    const paymentIntent2C2PPayload = response.data?.create2C2PPaymentIntent;

    const paymentLink = paymentIntent2C2PPayload?.paymentLink;
    const paymentTokenResponse = paymentIntent2C2PPayload?.paymentToken;
    const expiryTimerResponse = paymentIntent2C2PPayload?.expiryTimer;

    // Scalability: skip payment gateway call
    if (paymentTokenResponse === TEST_RECORD_PAYMENT_TOKEN) {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
      };
    }

    if (!paymentLink) {
      throw new PaymentDefaultError(paymentIntent2C2PPayload?.message);
    }

    setToken(paymentTokenResponse);

    setQrisData({
      qrImageUrl: paymentLink,
      qrExpiry: expiryTimerResponse,
      paymentToken: paymentTokenResponse,
      reference: referenceId,
      redirectUrl: frontendReturnUrl,
    });

    const paymentStatus = await checkPaymentStatus();

    return {
      status: paymentStatus as PaymentStatus,
    };
  };

  const submitQrisPaymentForInvoice = async (): Promise<HirerPaymentResult> => {
    resetStates();
    // TODO: Tracking

    if (isMaintenance) {
      throw new PaymentDefaultError('Payment method is in maintenance');
    }

    const frontendReturnUrl = getReturnUrl(
      PAYMENT_GATEWAY['2c2p'],
      [redirectQrisQuery],
      useCase,
      env,
    );

    const invoices = invoice.map((i) => ({
      reference: i.id,
      amount: i.amount,
    }));

    const inputVariables: ICreate2C2PPaymentIntentForInvoicePayload = {
      paymentMethodType: 'QR',
      frontendReturnUrl,
      channelCode: 'SHPQR',
      seekBillingAccountId: customerId, // TODO: need to update graphql to take in customerId instead of seekBillingAccountId
      seekAdvertiserId: accountId, // TODO: need to update graphql to take in accountId instead of seekAdvertiserId
      prn,
      totalForPaymentIntent,
      invoices,
      email,
    };

    const response = await create2C2PInvoicePaymentIntent({
      variables: {
        input: inputVariables,
      },
    });

    const paymentIntent2C2PPayload =
      response.data?.create2C2PPaymentIntentForInvoices;

    const paymentLink = paymentIntent2C2PPayload?.paymentLink;
    const paymentTokenResponse = paymentIntent2C2PPayload?.paymentToken;
    const expiryTimerResponse = paymentIntent2C2PPayload?.expiryTimer;

    // Scalability: skip payment gateway call
    if (paymentTokenResponse === TEST_RECORD_PAYMENT_TOKEN) {
      return {
        status: 'succeeded' as PaymentStatus,
        paymentIntentId: TEST_RECORD_SUCCEEDED_STATUS,
      };
    }

    if (!paymentLink) {
      throw new PaymentDefaultError(paymentIntent2C2PPayload?.message);
    }

    setToken(paymentTokenResponse);

    setQrisData({
      qrImageUrl: paymentLink,
      qrExpiry: expiryTimerResponse,
      paymentToken: paymentTokenResponse,
      reference: referenceId,
      redirectUrl: frontendReturnUrl,
    });

    const paymentStatus = await checkPaymentStatus();

    return {
      status: paymentStatus as PaymentStatus,
    };
  };

  const resetStates = () => {
    setQrisData(undefined);
    setIsClosed(false);
    setIsExpired(false);
    setToken('');
  };

  const onCloseDialog = (isExpiredFromModal: boolean) => {
    setQrisData(undefined);
    setIsClosed(true);
    setIsExpired(isExpiredFromModal);
  };

  const checkPaymentStatus = async () => {
    for (let count = 0; count < 60; count++) {
      await wait(INTERVAL);

      validateQRModalIsExpired();

      validateQRModalIsClosed();

      const response = await getPaymentInquiry({
        variables: {
          paymentToken: getToken(),
          currency: 'IDR',
        },
      });

      const status = response.data?.payment2c2pInquiryForInvoices?.status;

      switch (status) {
        case 'succeeded':
          return status;
        case 'pending':
        case 'processing':
          break;
        case 'cancelled':
          throw new PaymentGeneralError(t('payment.cancel.error'));
        default:
          throw new PaymentGeneralError(t('payment.cancel.error'));
      }
    }

    return 'requires_action';
  };

  return {
    submitQrisPayment,
    submitQrisPaymentForInvoice,
    onCloseDialog,
  };
};

export default useQris;
